import React, { useEffect } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import moment from 'moment-timezone';
import { get, isNil } from 'lodash';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { IntlShape, injectIntl } from 'react-intl';
import WarningCard from 'components/InvoiceFeeNoteDialog/WarningCard';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Typography, Grid } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import CloseIcon from '@material-ui/icons/Close';
import { StyledDialog } from 'components/InvoiceFeeNoteDialog/styles';
import { translate } from 'containers/services/translate';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import messages from 'components/InvoiceFeeNoteDialog/messages';
import { DATE_FORMAT, DATE_PICKET_FORMAT } from 'constants/global';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import { DOCUMENT_TYPES } from 'constants/global';
import { HOLDER_PV_PROVIDER } from 'containers/RecapPage/canstants';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { EMPTY_DOCUMENT } from 'containers/data-structure';
import InfoIcon from 'images/info-ic.svg';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { LIMIT_DATE_INVOICE } from 'containers/LawyerFeeNote/constants';
import { compareDate } from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';

export interface InvoiceInfoDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  handleValidate: Function;
  invoiceNumber: any;
  invoiceDate: any;
  setInvoiceNumber: Function;
  setInvoiceDate: Function;
  invoiceDocument: any;
  setInvoiceDocument: Function;
  resetUploadedDocument: Function;
  uploadDocument: Function;
  uploadedDocument: any;
  invoiceReceptionDate?: any;
  setInvoiceReceptionDate?: Function;
  isManager?: boolean;
  claimReceptionDate?: any;
  fillReceptionDate?: boolean;
  pvHolder?: any;
  limitInvoiceDate?: any;
  displayUpdateTitle?: boolean;
  docId?: string;
  setDocumentId?: Function;
}

const InvoiceInfoDialog: React.FC<InvoiceInfoDialogProps> = (
  props,
): JSX.Element => {
  const { onClose, open } = props;
  useEffect(() => {
    if (
      props.fillReceptionDate &&
      props.claimReceptionDate &&
      props.setInvoiceReceptionDate
    ) {
      const theDate = getDateIgnoreTimezone(props.claimReceptionDate);
      props.setInvoiceReceptionDate(
        moment(theDate as Date).format(DATE_FORMAT),
      );
    }
  }, [props.claimReceptionDate]);

  const handleClose = () => {
    onClose(false);
  };

  const handleChangeInvoiceNumber = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    props.setInvoiceNumber(event.target.value);
  };

  const handleChangeInvoiceDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const invoiceDate = moment(theDate as Date).format(DATE_FORMAT);
    props.setInvoiceDate(invoiceDate);
  };

  const handleChangeInvoiceReceptionDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    const invoiceDate = moment(theDate as Date).format(DATE_FORMAT);
    props.setInvoiceReceptionDate && props.setInvoiceReceptionDate(invoiceDate);
  };
  const isIntervenant = currentIntervenantStorage?.get();

  return (
    <StyledDialog onClose={handleClose} open={open}>
      <DialogTitle>
        <Grid
          container
          direction="row"
          alignItems="center"
          justify="space-between"
        >
          <Grid item xs={9}>
            <span
              style={{
                color: '#172C51 !important',
                fontSize: '20px',
                lineHeight: '24.42px',
              }}
            >
              {translate(messages.dialogInfoTitle, props)}
            </span>
          </Grid>
          <Grid item>
            <CloseIcon onClick={handleClose} />
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Grid
          container
          direction="row"
          alignItems="center"
          alignContent="center"
          style={{
            gap: 10,
            background: '#E3F2FD',
            borderRadius: 8,
            padding: '3px',
          }}
        >
          <Grid item>
            <img src={InfoIcon} alt="infoIcon" />
          </Grid>
          <Grid item>
            <Typography className="success-title">
              {translate(
                isIntervenant
                  ? props.displayUpdateTitle
                    ? messages.dialogInfoSubUpdateTitle
                    : messages.dialogInfoSubTitle
                  : messages.dialogInfoManagerSubTitle,
                props,
              )}
            </Typography>
          </Grid>
        </Grid>
        <div style={{ marginTop: 10 }}>
          {compareDate(
            moment().format('YYYY-MM-DD'),
            moment(props.invoiceDate).format('YYYY-MM-DD'),
          ) >= props.limitInvoiceDate &&
            !isNil(props.invoiceDate) &&
            !isIntervenant &&
            featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) && (
              <WarningCard
                message={translate(messages.warningInvoiceDateManager, props, {
                  limitDaysManager: props.limitInvoiceDate,
                })}
              />
            )}
        </div>
        <Grid container direction="row" spacing={3} style={{ marginTop: 10 }}>
          <Grid item xs={6}>
            <TextField
              label={translate(messages.invoiceNumberField, props)}
              name={'INVOICE_NUMBER'}
              value={props.invoiceNumber}
              fullWidth
              onChange={handleChangeInvoiceNumber}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                format={DATE_PICKET_FORMAT}
                id="invoice-date"
                fullWidth
                onChange={handleChangeInvoiceDate}
                value={dateStringToLocalDate(props.invoiceDate)}
                label={translate(messages.invoiceDateField, props)}
                error={
                  featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
                  isIntervenant &&
                  compareDate(
                    moment().format('YYYY-MM-DD'),
                    props.invoiceDate,
                  ) >= props.limitInvoiceDate
                }
                helperText={
                  featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
                  isIntervenant &&
                  !isNil(props.invoiceDate) &&
                  compareDate(
                    moment().format('YYYY-MM-DD'),
                    props.invoiceDate,
                  ) >= props.limitInvoiceDate &&
                  translate(messages.errorInvoiceDateIntervenant, props, {
                    limitDaysIntervenant: props.limitInvoiceDate,
                  })
                }
              />
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>
        {props.isManager && (
          <Grid xs={6} style={{ marginTop: 15 }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                format={DATE_PICKET_FORMAT}
                id="invoice-date"
                fullWidth
                onChange={handleChangeInvoiceReceptionDate}
                value={dateStringToLocalDate(props.invoiceReceptionDate)}
                label={translate(messages.invoiceReceptionDateField, props)}
              />
            </MuiPickersUtilsProvider>
          </Grid>
        )}
        <Grid xs={6} style={{ marginTop: 20 }}>
          <UploadDocumentBloc
            index={0}
            document={props.invoiceDocument}
            setDocument={props.setInvoiceDocument}
            resetUploadedDocument={props.resetUploadedDocument}
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            docType={DOCUMENT_TYPES.INVOICE_DOCUMENT}
            docId={props.docId}
            setDocumentId={props.setDocumentId}
            deleteDoc={() =>
              props.setInvoiceDocument({
                ...EMPTY_DOCUMENT,
              })
            }
          />
        </Grid>
      </DialogContent>
      <DialogActions className="success-actions">
        <SecondaryButton
          onClick={() => props.onClose()}
          disableElevation
          data-testId="cancel-button"
          title={translate(messages.cancel, props)}
        />
        <PrimaryButton
          onClick={() => props.handleValidate()}
          disableElevation
          data-testId="validate-button"
          className="primary-button"
          title={translate(messages.confirm, props)}
          disabled={
            (!isNil(
              get(currentIntervenantStorage?.get(), 'sagis_code', null),
            ) &&
              (isNil(props.invoiceNumber) ||
                isNil(props.invoiceDate) ||
                props.invoiceDate === 'Invalid date' ||
                isNil(get(props.invoiceDocument, 'url')) ||
                get(props.invoiceDocument, 'url') === '' ||
                props.invoiceNumber === '' ||
                (featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
                  compareDate(
                    moment().format('YYYY-MM-DD'),
                    props.invoiceDate,
                  ) >= props.limitInvoiceDate))) ||
            (props.pvHolder === HOLDER_PV_PROVIDER &&
              props.claimReceptionDate &&
              new Date(props.claimReceptionDate) >=
                new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')) &&
              (isNil(props.invoiceNumber) ||
                isNil(props.invoiceDate) ||
                props.invoiceDate === 'Invalid date' ||
                isNil(get(props.invoiceDocument, 'url')) ||
                get(props.invoiceDocument, 'url') === '' ||
                props.invoiceNumber === '')) ||
            (props.fillReceptionDate &&
              (isNil(props.invoiceNumber) ||
                isNil(props.invoiceDate) ||
                props.invoiceDate === 'Invalid date' ||
                isNil(get(props.invoiceDocument, 'url')) ||
                get(props.invoiceDocument, 'url') === '' ||
                props.invoiceNumber === ''))
          }
        />
      </DialogActions>
    </StyledDialog>
  );
};

export default injectIntl(InvoiceInfoDialog);
